import {
    ApplePayPayment,
    ApplePayPaymentRebranding,
    BancontactPayment,
    BancontactPaymentRebranding,
    BoletoPayment,
    BoletoPaymentRebranding,
    Cheque,
    ChequeRebranding,
    CreditCardBack,
    CreditCardBackRebranding,
    GooglePayPayment,
    GooglePayPaymentRebranding,
    IdealPayment,
    IdealPaymentRebranding,
    OxxoPayment,
    OxxoPaymentRebranding,
    PagofacilPayment,
    PagofacilPaymentRebranding,
    PaypalPayment,
    PaypalPaymentRebranding,
    RapipagoPayment,
    RapipagoPaymentRebranding,
    Receipt,
    SepaPayment,
    SepaPaymentRebranding,
    SofortPayment,
    SofortPaymentRebranding,
    StripePaymentRebranding,
    Voucher,
    VoucherRebranding,
} from '@eventbrite/eds-iconography';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React from 'react';
import { getIsAppRebrandActive } from '../../selectors/app';
import { CheckoutState } from '../../state';
import { PaymentMethodType } from './enums';
import {
    PaymentMethodMetadata,
    PAYMENT_METHODS_METADATA,
} from './paymentMethodMetadata';

const isAppRebrandActive = getIsAppRebrandActive({} as CheckoutState);
export interface PaymentMethodInfo extends PaymentMethodMetadata {
    userString: string | GenericLazyString | React.ReactElement;
    icon: React.ReactNode;
}

export const PAYMENT_METHODS_INFO = {
    [PaymentMethodType.CREDIT]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.CREDIT],
        userString: gettext('Credit or debit card'),
        icon: isAppRebrandActive ? (
            <CreditCardBackRebranding />
        ) : (
            <CreditCardBack />
        ),
    },
    [PaymentMethodType.IDEAL]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.IDEAL],
        userString: gettext('iDEAL'),
        icon: isAppRebrandActive ? (
            <IdealPaymentRebranding />
        ) : (
            <IdealPayment />
        ),
    },
    [PaymentMethodType.MAESTRO_BANCONTACT]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.MAESTRO_BANCONTACT],
        userString: gettext('Bancontact'),
        icon: isAppRebrandActive ? (
            <BancontactPaymentRebranding />
        ) : (
            <BancontactPayment />
        ),
    },
    [PaymentMethodType.SOFORT]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.SOFORT],
        userString: gettext('Sofort'),
        icon: isAppRebrandActive ? (
            <SofortPaymentRebranding />
        ) : (
            <SofortPayment />
        ),
    },
    [PaymentMethodType.NONCE_BRAINTREE]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.NONCE_BRAINTREE], //defaultusercountry env.countrycode
        userString: gettext('PayPal'),
        icon: isAppRebrandActive ? (
            <PaypalPaymentRebranding />
        ) : (
            <PaypalPayment />
        ),
    },
    [PaymentMethodType.SEPA_DIRECT_DEBIT]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.SEPA_DIRECT_DEBIT],
        userString: gettext('Sepa Direct Debit'),
        icon: isAppRebrandActive ? <SepaPaymentRebranding /> : <SepaPayment />,
    },
    [PaymentMethodType.CASH]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.CASH],
        userString: gettext('Pay at the door'),
        icon: isAppRebrandActive ? <VoucherRebranding /> : <Voucher />,
    },
    [PaymentMethodType.INVOICE]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.INVOICE],
        userString: gettext('Pay by invoice'),
        icon: isAppRebrandActive ? <Receipt /> : <Receipt />,
    },
    [PaymentMethodType.CHECK]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.CHECK],
        userString: gettext('Pay by check'),
        icon: isAppRebrandActive ? <ChequeRebranding /> : <Cheque />,
    },
    [PaymentMethodType.FPP_PAYPAL]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.FPP_PAYPAL],
        userString: gettext('PayPal'),
        icon: isAppRebrandActive ? (
            <PaypalPaymentRebranding />
        ) : (
            <PaypalPayment />
        ),
    },
    [PaymentMethodType.BOLETO_BANCARIO]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.BOLETO_BANCARIO],
        userString: gettext('Boleto Bancario'),
        icon: isAppRebrandActive ? (
            <BoletoPaymentRebranding />
        ) : (
            <BoletoPayment />
        ),
    },
    [PaymentMethodType.BARCODE]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.BARCODE],
        userString: gettext('Pay in cash with Rapipago or Pagofacil'),
        icon: isAppRebrandActive ? <OxxoPaymentRebranding /> : <OxxoPayment />,
    },
    [PaymentMethodType.RAPIPAGO]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.RAPIPAGO],
        userString: gettext('Pay in cash with Rapipago'),
        icon: isAppRebrandActive ? (
            <RapipagoPaymentRebranding />
        ) : (
            <RapipagoPayment />
        ),
    },
    [PaymentMethodType.PAGOFACIL]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.PAGOFACIL],
        userString: gettext('Pay in cash with Pagofacil'),
        icon: isAppRebrandActive ? (
            <PagofacilPaymentRebranding />
        ) : (
            <PagofacilPayment />
        ),
    },
    [PaymentMethodType.OXXO]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.OXXO],
        userString: gettext('Pay in cash with OXXO.'),
        icon: isAppRebrandActive ? <OxxoPaymentRebranding /> : <OxxoPayment />,
    },
    [PaymentMethodType.AUTHNET]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.AUTHNET],
        userString: gettext('Authorize.net'),
        icon: isAppRebrandActive ? (
            <CreditCardBackRebranding />
        ) : (
            <CreditCardBack />
        ),
    },
    [PaymentMethodType.APPLE_PAY]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.APPLE_PAY],
        userString: gettext('Apple Pay'),
        icon: isAppRebrandActive ? (
            <ApplePayPaymentRebranding />
        ) : (
            <ApplePayPayment />
        ),
    },
    [PaymentMethodType.GOOGLE_PAY]: {
        ...PAYMENT_METHODS_METADATA[PaymentMethodType.GOOGLE_PAY],
        userString: gettext('Google Pay'),
        icon: isAppRebrandActive ? (
            <GooglePayPaymentRebranding />
        ) : (
            <GooglePayPayment />
        ),
    },
    [PaymentMethodType.STRIPE_CONFIRMATION_TOKEN]: {
        ...PAYMENT_METHODS_METADATA[
            PaymentMethodType.STRIPE_CONFIRMATION_TOKEN
        ],
        userString: gettext('Other methods (aka Stripe)'),
        icon: isAppRebrandActive ? (
            <StripePaymentRebranding />
        ) : (
            <div>Icon</div>
        ),
    },
} as const;
